import { useEffect, useMemo, useState } from "react";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import MuiSelect, { SelectProps } from "@mui/material/Select";
import Box, { BoxProps } from "@mui/system/Box";

export type Option<T> = {
  value: T;
  label: string;
  group?: string;
};

export type OptionGroup<T> = {
  label?: string;
  options: Option<T>[];
};

interface Props<T> extends Omit<BoxProps, "onChange"> {
  placeholder?: string;
  options?: OptionGroup<T>[];
  selectedOption?: Option<T>;
  portalWidth?: string;
  onChange: (selectedOption: Option<T> | null) => void;
  selectProps?: SelectProps;
}

export const Select = <T extends string | number>(props: Props<T>) => {
  const {
    placeholder,
    selectedOption,
    options,
    onChange,
    selectProps,
    ...layoutProps
  } = props;

  const allOptions = useMemo(
    () =>
      options
        ?.flatMap((group) =>
          group.options.map((option) => ({
            ...option,
            group: group.label,
          }))
        )
        .sort((a, b) => (a.group ?? "").localeCompare(b.group ?? "")) || [],
    [options]
  );

  const [value, setValue] = useState<T | "">(
    selectedOption ? selectedOption.value : ""
  );

  useEffect(() => {
    setValue(selectedOption ? selectedOption.value : "");
  }, [selectedOption]);

  const handleChange = (event: any) => {
    const value = event.target.value as T;
    setValue(value);
    onChange(allOptions.find((option) => option.value === value) || null);
  };

  return (
    <Box {...layoutProps}>
      <FormControl fullWidth size="small">
        <InputLabel>{placeholder}</InputLabel>
        <MuiSelect
          {...selectProps}
          value={value}
          label={placeholder}
          sx={{ flex: 1 }}
          onChange={handleChange}
        >
          {allOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    </Box>
  );
};
